import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { InMemoryCacheService, MessageType, Organization, User, UtilsService } from '../..';

import { Location } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class AuthGuardService  {

  constructor(private router: Router, private cache: InMemoryCacheService, private utils: UtilsService,
    private location: Location) {}

  public async canActivate(_route?: ActivatedRouteSnapshot, state?: RouterStateSnapshot): Promise<boolean> {
    const requestedPath = (state?.url ?? this.location.path() ?? '').split('?')[0].toLowerCase();
    const currentPath = requestedPath.endsWith('/') ? requestedPath.slice(0, -1) : requestedPath;
    const isBillingFlow = currentPath.startsWith('/billing');
    const isAccountManagement = ['/org/settings/billing', '/org/settings/danger', '/settings/profile'].includes(currentPath);
    const isAccessDenied = currentPath.startsWith('/access-denied');
    const isOrgInactive = currentPath.startsWith('/organization/subscription-inactive');

    const user: User = this.cache.user;
    const organization: Organization = this.cache.auth?.organization;
    const token = this.utils.parseJwt(this.cache?.auth?.token);
    const isSuperUser: boolean = token?.Role === 'SuperUser';

    if (!user || !organization) {
      this.utils.logger.info(`AuthGuardService.canActivate() - No user, redirecting to login`);
      this.cache.logout();
      return false;
    }

    // Route-specific membership/permission guards still protect organization management.
    if (isAccountManagement) return true;

    if (!isSuperUser) {
      const subscriptionStatus = (organization?.subscriptionStatus || '').toLowerCase();
      const hasSubscription = !!organization?.subscriptionId;
      const isPastDue = subscriptionStatus === 'past_due';
      const isCanceled = subscriptionStatus === 'canceled';
      const isPersonal = organization?.isPersonal !== false;

      if (isPersonal && isPastDue) {
        if (isBillingFlow) {
          return true;
        }
        this.utils.logger.info(`AuthGuardService.canActivate() - Org subscription past due, redirecting to add payment method`);
        this.utils.errorMessage(MessageType.ALERT, `Your subscription is past due. You must have an active subscription to access application. Would you like to add a payment method?.`);
        this.router.navigate(['/billing/addpaymentmethod']);
        return false;
      }

      if (isPersonal && (!hasSubscription || isCanceled)) {
        if (isBillingFlow || isAccessDenied) {
          return true;
        }
        this.utils.errorMessage(MessageType.ALERT, `You must have a subscription to access application.`);
        this.utils.logger.info(`AuthGuardService.canActivate() - No subscription, redirecting to subscription`);
        this.router.navigate([isCanceled && hasSubscription ? '/org/settings/billing' : '/billing/subscription']);
        return false;
      }

      if (!isPersonal && (!hasSubscription || isPastDue || isCanceled)) {
        if (isOrgInactive || isAccessDenied) {
          return true;
        }
        this.utils.logger.info(`AuthGuardService.canActivate() - Organization subscription inactive, redirecting to inactive page`);
        this.router.navigate(['/organization/subscription-inactive']);
        return false;
      }
    }

    return true;
  }

}
